import {
  Timestamp,
  arrayUnion,
  deleteDoc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  updateProfile,
} from "firebase/auth";
import { getDb, getSecondaryAuth } from "@/lib/firebase/client";
import { normalizeName } from "@/lib/utils/strings";
import { paths, refCol, refDoc } from "./paths";
import {
  memberSchema,
  type Member,
  type Role,
  avatarColors,
} from "@/lib/schemas";

export async function listMembers(hid: string): Promise<Member[]> {
  const db = getDb();
  const snap = await getDocs(refCol(db, paths.members(hid)));
  return snap.docs.flatMap((d) => {
    const parsed = memberSchema.safeParse({ userId: d.id, ...d.data() });
    return parsed.success ? [parsed.data] : [];
  });
}

export async function getMember(
  hid: string,
  uid: string,
): Promise<Member | null> {
  const db = getDb();
  const snap = await getDoc(refDoc(db, paths.member(hid, uid)));
  if (!snap.exists()) return null;
  const parsed = memberSchema.safeParse({ userId: snap.id, ...snap.data() });
  return parsed.success ? parsed.data : null;
}

type AddMemberInput = {
  hid: string;
  uid: string;
  displayName: string;
  role: Role;
  avatarColor?: (typeof avatarColors)[number];
};

export async function addMember(input: AddMemberInput): Promise<void> {
  const db = getDb();
  await setDoc(refDoc(db, paths.member(input.hid, input.uid)), {
    userId: input.uid,
    displayName: input.displayName,
    role: input.role,
    avatarColor: input.avatarColor ?? "amber",
    balanceMinor: 0,
    createdAt: serverTimestamp(),
  });
  // Mapping users/{uid} → householdId
  await setDoc(refDoc(db, paths.user(input.uid)), {
    householdId: input.hid,
    role: input.role,
  });
}

type CreateMemberInput = {
  hid: string;
  /**
   * Tryb tworzenia konta:
   *  - email: klasyczne email+hasło (rodzic/opiekun, dziecko z mailem)
   *  - username: dziecko bez maila — fake-email
   *    `<username>@<householdSlug>.kids.sianko.local` budowany pod spodem.
   *    Wymaga już ustawionego household.slug (rodzic w Settings → "Pseudonim rodziny").
   */
  identity:
    | { kind: "email"; email: string }
    | { kind: "username"; username: string; householdSlug: string };
  password: string;
  displayName: string;
  role: Role;
  /** Etykieta typu „Mama", „Babcia". Opcjonalna. */
  relation?: string;
  avatarColor?: (typeof avatarColors)[number];
  /**
   * Rok urodzenia (4-cyfrowy). Optional — pytamy w AddChildSheet, używamy
   * do preset wiekowych chore'ów. Zapis konwertowany jako `currentYear - age`
   * po stronie wywołującego. Walidacja zakresu w schema (`memberSchema`).
   */
  birthYear?: number;
};

/**
 * Domena fake-emaili dla dzieci logujących się przez username+slug.
 * Konwencja stała — nie zmieniać bez migracji wszystkich kont.
 */
export const KIDS_EMAIL_DOMAIN = "kids.sianko.local";

export function buildKidsFakeEmail(
  username: string,
  householdSlug: string,
): string {
  return `${username}@${householdSlug}.${KIDS_EMAIL_DOMAIN}`;
}

/** Patch profilu domownika (relation, displayName, avatarColor). */
export async function updateMember(
  hid: string,
  uid: string,
  patch: Partial<Pick<Member, "displayName" | "relation" | "avatarColor">>,
): Promise<void> {
  const db = getDb();
  const normalized = patch.displayName
    ? { ...patch, displayName: normalizeName(patch.displayName) }
    : patch;
  await updateDoc(refDoc(db, paths.member(hid, uid)), normalized);
}

/**
 * Usuwa member doc z household. Rules: tylko admin, target nie może być
 * adminem (ochrona założyciela rodziny).
 *
 * Skutki uboczne których NIE robimy z klienta (wymagają Cloud Function /
 * Admin SDK):
 *   - Firebase Auth user pozostaje (dziecko nie może już się zalogować
 *     i tak — brak member docu odetnie mu UI rules)
 *   - users/{uid} mapping pozostaje (rule `delete: if false`)
 *   - completions / transactions / payouts z userId tego usera zostają
 *     jako audit trail
 *
 * Wystarczające na MVP — z perspektywy rodzica osoba „znika" z rodziny.
 */
export async function deleteMember(hid: string, uid: string): Promise<void> {
  const db = getDb();
  await deleteDoc(refDoc(db, paths.member(hid, uid)));
}

/**
 * Tworzy konto domownika (dziecko / drugi rodzic / opiekun): Auth user
 * (przez secondary instancję, żeby nie wylogować zalogowanego rodzica)
 * + users/{uid} mapping + members/{uid} doc.
 *
 * Wymaga aktualnie zalogowanego rodzica.
 */
export async function createMemberAccount(
  input: CreateMemberInput,
): Promise<{ uid: string }> {
  const secondaryAuth = getSecondaryAuth();
  const isUsernameMode = input.identity.kind === "username";
  const authEmail =
    input.identity.kind === "email"
      ? input.identity.email
      : buildKidsFakeEmail(
          input.identity.username,
          input.identity.householdSlug,
        );
  const cred = await createUserWithEmailAndPassword(
    secondaryAuth,
    authEmail,
    input.password,
  );
  // Normalizuj imię w jednym miejscu — niech dziecko/rodzic nie chodzi
  // z "kasia" zapisanym w bazie tylko dlatego, że tak wpisali w formularzu.
  const displayName = normalizeName(input.displayName);
  try {
    await updateProfile(cred.user, { displayName });
  } catch {
    // Niekrytyczne
  }

  const db = getDb();
  const batch = writeBatch(db);
  batch.set(refDoc(db, paths.member(input.hid, cred.user.uid)), {
    userId: cred.user.uid,
    displayName,
    role: input.role,
    ...(input.relation ? { relation: input.relation } : {}),
    ...(isUsernameMode && input.identity.kind === "username"
      ? { username: input.identity.username }
      : {}),
    ...(input.birthYear != null ? { birthYear: input.birthYear } : {}),
    avatarColor:
      input.avatarColor ?? (input.role === "parent" ? "sky" : "amber"),
    balanceMinor: 0,
    createdAt: serverTimestamp(),
  });
  batch.set(refDoc(db, paths.user(cred.user.uid)), {
    householdId: input.hid,
    role: input.role,
  });
  await batch.commit();

  // E-mail weryfikacyjny tylko gdy konto faktycznie ma prawdziwy email
  // (tryb "email"). Username-mode używa fake-emaili `@kids.sianko.local`,
  // gdzie wysłanie maila byłoby albo bouncem albo errorem — nie ma sensu.
  if (input.identity.kind === "email") {
    try {
      await sendEmailVerification(cred.user, {
        url: "https://app.sianko.pl/auth-action",
        handleCodeInApp: false,
      });
    } catch (e) {
      // Niekrytyczne — konto utworzone, można później wysłać ponownie z banneru
      console.warn("createMemberAccount: sendEmailVerification failed", e);
    }
  }

  // Wyloguj secondary, żeby nie wisiał stan dla nowego konta
  await signOut(secondaryAuth);

  return { uid: cred.user.uid };
}

/**
 * Heartbeat „użytkownik wszedł do aplikacji" — pisze serverTimestamp do
 * members/{uid}.lastActiveAt. Klient throttluje się in-memory: ten sam uid
 * nie pisze częściej niż co `THROTTLE_MS`. Zapis nieblokujący — błąd
 * sieci/uprawnień nie powinien przerwać UI.
 *
 * Wywoływany z AuthGuard po każdej zmianie kontekstu auth. Wynikiem jest
 * cheap DAU/WAU/MAU dla panelu admin (lekkie pole, ~1 write/sesja/user).
 *
 * Rules: self-update members/{uid} dopuszcza zapis dowolnego pola poza
 * role/balance/isAdmin — `lastActiveAt` przechodzi bez zmiany rules.
 */
const ACTIVE_THROTTLE_MS = 5 * 60 * 1000;
const lastActiveMarked = new Map<string, number>();

export async function markActive(hid: string, uid: string): Promise<void> {
  const now = Date.now();
  const cacheKey = `${hid}:${uid}`;
  const prev = lastActiveMarked.get(cacheKey);
  if (prev && now - prev < ACTIVE_THROTTLE_MS) return;
  lastActiveMarked.set(cacheKey, now);
  try {
    const db = getDb();
    await updateDoc(refDoc(db, paths.member(hid, uid)), {
      lastActiveAt: serverTimestamp(),
    });
  } catch {
    // Niekrytyczne — telemetria nie blokuje UI. Cofamy cache żeby spróbować
    // przy kolejnym mount'cie (np. przejściowy network error).
    lastActiveMarked.delete(cacheKey);
  }
}

/**
 * Dodaje uratowane dni do member.streakShieldRescues (idempotentny arrayUnion).
 * Wołać tylko z hooka liczącego streak — wymaga uprawnień write na sobie
 * (security rules: child może updatować swój profil bez balanceMinor/role).
 */
export async function addStreakShieldRescues(
  hid: string,
  uid: string,
  dates: Date[],
): Promise<void> {
  if (dates.length === 0) return;
  const db = getDb();
  await updateDoc(refDoc(db, paths.member(hid, uid)), {
    streakShieldRescues: arrayUnion(
      ...dates.map((d) => Timestamp.fromDate(d)),
    ),
  });
}

/** Backward-compat: tworzy konto dziecka z emailem (legacy callers). */
export async function createChildAccount(input: {
  hid: string;
  email: string;
  password: string;
  displayName: string;
  avatarColor?: (typeof avatarColors)[number];
}): Promise<{ uid: string }> {
  return createMemberAccount({
    hid: input.hid,
    identity: { kind: "email", email: input.email },
    password: input.password,
    displayName: input.displayName,
    avatarColor: input.avatarColor,
    role: "child",
  });
}
